import { inject } from '@angular/core';
import { CanActivateFn, ActivatedRouteSnapshot, RouterStateSnapshot, UrlTree } from '@angular/router';
import { tap } from 'rxjs/operators';
import { AppConstants } from '../../../app.constants';
import { SharedService } from '@services';
import { UserAccessLevel } from '@interfaces';
import { AuthService, BenefitManagementService,  ErrorManagerService, ErrorType } from '@core';


export const AccessLevelGuard: CanActivateFn = async (
  next: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
): Promise<boolean | UrlTree> => {
  // Inject services using the inject function
  const errorManager = inject(ErrorManagerService);
  const benefitService = inject(BenefitManagementService);
  const sharedService = inject(SharedService);
  const authService = inject(AuthService);

  // Fetch client data
  const clients = await fetchClientData(benefitService);

  // Access levels from route data
  const accessLevels = next.data.accessLevels;
  let accessLevelToCheck;
  sharedService.accessLevel$.subscribe((v) => (accessLevelToCheck = v));

  // If a clientId exists in queryParams, update access level
  if (next.queryParams.clientId) {
    const client = clients.find((e) => e.clientId === next.queryParams.clientId);
    sharedService.updateAccessLevel(client?.olA_Access_Type);

    if (client?.groupLevelFlag) {
      await sharedService
        .getUserGroupsAccess({ emailId: authService.userEmail, clientId: client.clientId })
        .pipe(
          tap((res) => {
            const grpAccess = res.find(
              (g) => g.groupId?.toUpperCase() === next.queryParams.groupNumber?.toUpperCase()
            );
            sharedService.updateAccessLevel(
              grpAccess?.benefitEligibilityAccessType ?? client.olA_Access_Type
            );
          })
        )
        .toPromise();
    }
  }

  // Check if route is for adding new member
  if (next.routeConfig?.path?.includes('add-new-member')) {
    checkIfHasAccessToAddMember(clients, errorManager);
  } else {
    if (!accessLevels.includes(accessLevelToCheck)) {
      showNoAccessMessage(errorManager);
      return false;
    }
  }

  // Return true to allow access
  return true;
};

// Fetch client data asynchronously
async function fetchClientData(benefitService: BenefitManagementService): Promise<any[]> {
  try {
    return await benefitService.getClientsListByAccess().toPromise();
  } catch (error) {
    console.error('Error fetching clients data:', error);
    return [];
  }
}

// Check if user has access to add a new member
function checkIfHasAccessToAddMember(clients: any[], errorManager: ErrorManagerService): void {
  const hasAccessToAddNewMember =
    clients.filter(
      (e) => e.olA_Access_Type !== UserAccessLevel.LEVEL_A && e.olA_Access_Type !== UserAccessLevel.LEVEL_E
    ).length !== 0;

  if (!hasAccessToAddNewMember) {
    showNoAccessMessage(errorManager);
  }
}

// Show no access message
function showNoAccessMessage(errorManager: ErrorManagerService): void {
  errorManager.throwError({
    type: ErrorType.ERROR_GENERIC,
    message: AppConstants.ERROR.MESSAGES.NO_ACCESS_TO_MODULE,
    component: 'AccessLevelGuard',
    title: '',
  });
}
